import { alpha, Box, keyframes, Stack, Typography, useTheme } from "@mui/material";
import { SenecEkpTheme } from "src/styles/ekp-theme/senecEkpTheme";

export interface StatusBarProps {
    /**Three possible States*/
    status: "Increasing" | "Decreasing" | "Static";
    statusLabel: string;
    /**Values below 0 will be set to 0, and Values above 100 to 100*/
    currentPercentage: number;
    /**SLOW = 3s, <br>NORMAL = 2s (Default), <br>FAST = 1s*/
    animationSpeed?: AnimationSpeed;
}

export enum AnimationSpeed {
    SLOW = 3,
    NORMAL = 2,
    FAST = 1,
}

export const StatusBar = ({ status, statusLabel, currentPercentage, animationSpeed = AnimationSpeed.NORMAL }: StatusBarProps) => {
    const theme: SenecEkpTheme = useTheme();
    const textPulsateAnimation = keyframes`
      0% {
        color: ${theme.palette.senecSecondary.neutralGray80};
      }
      50% {
        color: ${theme.palette.senecSecondary.neutralGray50};
      }
      100% {
        color: ${theme.palette.senecSecondary.neutralGray80};
      }
    `;

    return (
        <Stack height={"32px"} justifyContent={"flex-end"} alignItems={"flex-end"} spacing={0.5}>
            <Typography
                variant={"subtitle1"}
                color={theme.palette.senecSecondary.neutralGray70}
                sx={{
                    textAlign: "right",
                    whiteSpace: "normal",
                    animation:
                        status != "Static" ? `${textPulsateAnimation} ${animationSpeed.valueOf()}s infinite linear` : "none",
                }}
            >
                {statusLabel}
            </Typography>
            <Box
                id={"outter-status-bar"}
                height={"4px"}
                borderRadius={1}
                bgcolor={theme.palette.senecPrimary.lightWarmGrey}
                width={"100%"}
            >
                {status === "Increasing" && (
                    <IncreasingStatusBar
                        theme={theme}
                        widthInPercentage={currentPercentage}
                        animationSpeed={animationSpeed.valueOf()}
                    />
                )}
                {status === "Decreasing" && (
                    <DecreasingStatusBar
                        theme={theme}
                        widthInPercentage={currentPercentage}
                        animationSpeed={animationSpeed.valueOf()}
                    />
                )}
                {status === "Static" && <StaticStatusBar theme={theme} widthInPercentage={currentPercentage} />}
            </Box>
        </Stack>
    );
};

const increasingAnimation = keyframes`
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
`;

const decreasingAnimation = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 100% 0;
  }
`;

const IncreasingStatusBar = ({
    theme,
    widthInPercentage,
    animationSpeed,
}: {
    theme: SenecEkpTheme;
    widthInPercentage: number;
    animationSpeed: number;
}) => (
    <Box
        data-testid={"increasing-status-bar"}
        sx={{
            height: "4px",
            borderRadius: 2,
            background: `linear-gradient(90deg, ${theme.palette.senecPrimary.deepBlue}, ${theme.palette.senecSecondary.limeGreen}, ${theme.palette.senecPrimary.deepBlue}, ${theme.palette.senecSecondary.limeGreen})`,
            backgroundSize: "300% 100%",
            animation: `${increasingAnimation} ${animationSpeed}s infinite linear`,
            width: widthInPercentage <= 100 ? (widthInPercentage < 0 ? "0%" : `${widthInPercentage}%`) : "100%",
        }}
    >
        <Box
            sx={{
                height: "4px",
                borderRadius: 2,
                background: `linear-gradient(90deg, rgba(0,0,0,0), ${alpha(
                    theme.palette.senecSecondary.limeGreen,
                    0.5,
                )}, rgba(0,0,0,0), ${alpha(theme.palette.senecSecondary.limeGreen, 0.5)})`,
                backgroundSize: "300% 100%",
                animation: `${increasingAnimation} ${animationSpeed}s infinite ease-in-out`,
                width: `100%`,
            }}
        />
    </Box>
);

const DecreasingStatusBar = ({
    theme,
    widthInPercentage,
    animationSpeed,
}: {
    theme: SenecEkpTheme;
    widthInPercentage: number;
    animationSpeed: number;
}) => (
    <Box
        data-testid={"decreasing-status-bar"}
        sx={{
            height: "4px",
            borderRadius: 2,
            background: `linear-gradient(90deg, ${theme.palette.senecPrimary.deepBlue}, ${theme.palette.senecSecondary.tarocco}, ${theme.palette.senecPrimary.deepBlue}, ${theme.palette.senecSecondary.tarocco})`,
            backgroundSize: "300% 100%",
            animation: `${decreasingAnimation} ${animationSpeed}s infinite linear`,
            width: widthInPercentage <= 100 ? (widthInPercentage < 0 ? "0%" : `${widthInPercentage}%`) : "100%",
        }}
    >
        <Box
            sx={{
                height: "4px",
                borderRadius: 2,
                background: `linear-gradient(90deg, rgba(0,0,0,0), ${alpha(
                    theme.palette.senecSecondary.tarocco,
                    0.25,
                )}, rgba(0,0,0,0), ${alpha(theme.palette.senecSecondary.tarocco, 0.25)})`,
                backgroundSize: "300% 100%",
                animation: `${decreasingAnimation} ${animationSpeed}s infinite ease-in-out`,
                width: `100%`,
            }}
        />
    </Box>
);

const StaticStatusBar = ({ theme, widthInPercentage }: { theme: SenecEkpTheme; widthInPercentage: number }) => (
    <Box
        data-testid={"static-status-bar"}
        sx={{
            height: "4px",
            borderRadius: 2,
            background: theme.palette.senecPrimary.deepBlue,
            width: widthInPercentage <= 100 ? (widthInPercentage < 0 ? "0%" : `${widthInPercentage}%`) : "100%",
        }}
    />
);
